/**
 * 模块注册中心
 * 
 * 负责注册、管理和查询所有工作台功能模块。
 * 每个模块通过 registerModule 注册，系统自动收集路由和菜单。
 */

import type { RouteRecordRaw } from 'vue-router'
import type { WorkbenchModule, ModuleMenuItem } from './types'

/** 已注册模块 Map */
const modules = new Map<string, WorkbenchModule>()

/** 模块注册中心 */
export const moduleRegistry = {
  /**
   * 注册一个工作台模块
   */
  register(module: WorkbenchModule): void {
    const { name } = module.meta

    if (modules.has(name)) {
      console.warn(`[ModuleRegistry] 模块 "${name}" 已注册，将被覆盖`)
    }

    modules.set(name, module)

    // 执行模块安装
    if (module.install) {
      try {
        module.install()
      } catch (e) {
        console.error(`[ModuleRegistry] 模块 "${name}" 安装失败:`, e)
      }
    }

    console.info(`[ModuleRegistry] 模块 "${name}" 已注册`)
  },

  /**
   * 批量注册模块
   */
  registerAll(moduleList: WorkbenchModule[]): void {
    moduleList.forEach((mod) => this.register(mod))
  },

  /**
   * 卸载一个模块
   */
  unregister(name: string): void {
    const module = modules.get(name)
    if (!module) {
      console.warn(`[ModuleRegistry] 模块 "${name}" 未注册`)
      return
    }

    if (module.uninstall) {
      try {
        module.uninstall()
      } catch (e) {
        console.error(`[ModuleRegistry] 模块 "${name}" 卸载失败:`, e)
      }
    }

    modules.delete(name)
    console.info(`[ModuleRegistry] 模块 "${name}" 已卸载`)
  },

  /**
   * 获取所有已注册模块
   */
  getAll(): WorkbenchModule[] {
    return Array.from(modules.values())
  },

  /**
   * 获取已启用模块
   */
  getEnabled(): WorkbenchModule[] {
    return this.getAll().filter((m) => m.meta.enabled !== false)
  },

  /**
   * 根据名称获取模块
   */
  get(name: string): WorkbenchModule | undefined {
    return modules.get(name)
  },

  /**
   * 检查模块是否已注册
   */
  has(name: string): boolean {
    return modules.has(name)
  },

  /**
   * 收集所有模块的路由（用于 Vue Router）
   */
  collectRoutes(): RouteRecordRaw[] {
    const routes: RouteRecordRaw[] = []
    this.getEnabled().forEach((module) => {
      routes.push(...module.routes)
    })
    return routes
  },

  /**
   * 收集所有模块的菜单项（用于侧边栏导航）
   */
  collectMenuItems(): ModuleMenuItem[] {
    const items: ModuleMenuItem[] = []
    this.getEnabled().forEach((module) => {
      if (module.menuItem) {
        items.push(module.menuItem)
      }
    })
    // 按 order 排序
    return items.sort((a, b) => (a.order ?? 100) - (b.order ?? 100))
  },

  /**
   * 清空所有模块
   */
  clear(): void {
    modules.forEach((_, name) => this.unregister(name))
  }
}
